import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { StyledLabel, StyledMandatory, StyledSupportingText } from '@components/Datepicker/DatepickerTsoPq';
import TooltipTsoPq from '@components/Tooltip/TooltipTsoPq';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ToggleButton, ToggleButtonGroup, Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

type Option = {
  segmentValue: string;
  segmentLabel: string | JSX.Element;
  icon?: JSX.Element;
  tooltip?: string;
};

interface CustomSegmentedButtonProps {
  options: Option[];
  sizeVariant: 's' | 'm' | 'l';
  multipleOptions?: boolean;
  error?: boolean;
  mandatory?: boolean;
  customWidth?: string;
  disabled?: boolean;
  defaultValue?: string;
  handleChange?: (value: string[]) => void;
  centerAlignment?: boolean;
  supportingText?: string;
  label?: string;
}

type StyledSupportingTextProps = TypographyProps & {
  error?: boolean;
};

const SIZE_HEIGHTS = {
  s: '1.75rem',
  m: '2.125rem',
  l: '2.625rem',
};

const getPaddingAndGap = (sizeVariant: 's' | 'm' | 'l') => {
  const spacing = tsoPqTheme.spacingValues.buttonSpacing;
  const sizeMap = {
    s: {
      gap: spacing.spacing_button_middle_button_md_gap,
    },
    m: {
      gap: spacing.spacing_button_middle_button_md_gap,
    },
    l: {
      gap: spacing.spacing_button_large_button_lg_gap,
    },
  };
  return sizeMap[sizeVariant] || sizeMap.l;
};

const StyledSegmentedButtonContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'flex-end',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
  };
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => !['sizeVariant', 'border', 'error'].includes(prop as string),
})<CustomSegmentedButtonProps>(({ error = false, ...props }) => {
  return {
    '& .MuiToggleButtonGroup-lastButton': {
      border: error
        ? `0.5px solid ${tsoPqTheme.palette.customError.color_signal_error_error}`
        : `0.5px solid ${tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_enabled}`,
      height: '2.27rem',
    },
    '& .MuiToggleButtonGroup-firstButton': {
      border: error
        ? `0.5px solid ${tsoPqTheme.palette.customError.color_signal_error_error}`
        : `0.5px solid ${tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_enabled}`,
      height: '2.27rem',
    },
    '& .MuiToggleButtonGroup-middleButton': {
      border: error
        ? `0.5px solid ${tsoPqTheme.palette.customError.color_signal_error_error}`
        : `0.5px solid ${tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_enabled}`,
      height: '2.27rem',
    },
  };
});

export const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'centerAlignment',
})<{ centerAlignment?: boolean }>(({ centerAlignment }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: centerAlignment ? 'center' : 'flex-start',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
}));

const StyledToggleButton = styled(ToggleButton, {
  shouldForwardProp: (prop) =>
    !['sizeVariant', 'customWidth', 'options', 'centerAlignment', 'error'].includes(prop as string),
})<CustomSegmentedButtonProps>(({ sizeVariant, customWidth, options, centerAlignment, error, ...props }) => {
  const { gap } = getPaddingAndGap(sizeVariant);

  return {
    width: `${Number.parseFloat(customWidth as string) / options.length}rem`,
    backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
    color: tsoPqTheme.palette.customTextColor?.color_font_ft_text,
    height: SIZE_HEIGHTS[sizeVariant],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'end',
    textTransform: 'none',
    gap,
    fontFamily: tsoPqTheme.typography.openSans,
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl,
      color: tsoPqTheme.palette.customTextColor?.color_font_ft_text,
    },
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
      transition: 'background-color 150ms ease-out',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&.MuiButton-root:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&.Mui-selected': {
      backgroundColor: tsoPqTheme.palette.background.primary?.color_primary_background_bg_prim_enabled_lt,
      color: tsoPqTheme.palette.customTextColor?.color_font_ft_text,
      '&:hover': {
        backgroundColor: tsoPqTheme.palette.background.primary?.color_primary_background_bg_prim_hover_lt,
      },
      '&:focus:': {
        transition: 'background-color 150ms ease-out',
        backgroundColor: tsoPqTheme.palette.background.primary?.color_primary_background_bg_prim_pressed_lt,
      },
    },
  };
});

const SegmentedButtonTsoPq: FC<CustomSegmentedButtonProps> = ({
  options,
  sizeVariant = 'l',
  multipleOptions = false,
  customWidth,
  disabled = false,
  defaultValue,
  centerAlignment = false,
  mandatory,
  error = false,
  supportingText,
  label = '',
  handleChange,
  ...props
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const fontSize = {
    s: tsoPqTheme.typography.fontsize_body_body_small,
    m: tsoPqTheme.typography.fontsize_body_body_medium,
    l: tsoPqTheme.typography.fontsize_body_body_large,
  };

  const handleSelection = (_event: React.MouseEvent<HTMLElement>, newValue: string | string[]) => {
    let updatedValues: string[];

    if (multipleOptions) {
      const newArray = Array.isArray(newValue) ? newValue : [newValue];
      updatedValues = newArray;
    } else {
      updatedValues = typeof newValue === 'string' ? [newValue] : newValue;
    }

    setSelectedValues((previous) => {
      if (previous.join('|') === updatedValues.join('|')) {
        return previous;
      }

      handleChange?.(updatedValues);
      return updatedValues;
    });
  };

  useEffect(() => {
    if (disabled) setSelectedValues([]);
  }, [disabled]);

  useEffect(() => {
    const nextValues = defaultValue ? [defaultValue] : [];

    setSelectedValues((previous) => (previous.join('|') === nextValues.join('|') ? previous : nextValues));
  }, [defaultValue]);

  return (
    <StyledSegmentedButtonContainer>
      <StyledLabel>
        {label}
        {mandatory && <StyledMandatory>*</StyledMandatory>}
      </StyledLabel>
      <StyledToggleButtonGroup
        value={selectedValues}
        exclusive
        onChange={handleSelection}
        aria-label="custom toggle button group"
        options={[]}
        error={error}
        sizeVariant={'s'}
        disabled={disabled}
        {...props}
      >
        {options.map((option) => (
          <StyledToggleButton
            options={options}
            key={option.segmentValue}
            value={option.segmentValue}
            sizeVariant={sizeVariant}
            customWidth={customWidth}
          >
            <TooltipTsoPq title={option.tooltip || ''}>
              <StyledContainer centerAlignment={centerAlignment}>
                {selectedValues.find((value: string) => value === option.segmentValue) && (
                  <FontAwesomeIcon icon={faCheck} size="lg" />
                )}
                <Typography
                  textAlign={centerAlignment ? 'center' : 'left'}
                  fontSize={fontSize[sizeVariant]}
                  fontFamily={tsoPqTheme.typography.openSans}
                >
                  {option.segmentLabel}
                </Typography>
              </StyledContainer>
            </TooltipTsoPq>
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
      {supportingText && <StyledSupportingText error={error}>{supportingText}</StyledSupportingText>}
    </StyledSegmentedButtonContainer>
  );
};

export default SegmentedButtonTsoPq;
